import { motion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import getPlatform from '../../../common/utils/getPlatform';
import rgbToHex from '../../../common/utils/rgbToHex';
import AppDownloadModal2 from '../../AppDownloadModal2';
import useBreakpoints from '../hooks/useBreakpoints';

const GIFT_VIDEO_WIDTH = 1280;
const GIFT_VIDEO_HEIGHT = 720;

const GIFT_SCALE = {
  desktop: 0.65,
  tablet: 0.5,
  mobile: 0.4,
};

export default function HeroSection() {
  const media = useBreakpoints({ defaultMedia: 'desktop' });
  const giftCanvasRef = useRef<HTMLCanvasElement>(null);
  const backgroundCanvasRef = useRef<HTMLCanvasElement>(null);
  const giftVideoRef = useRef<HTMLVideoElement>();
  const [isAppDownloadModalVisible, setIsAppDownloadModalVisible] = useState(false);

  useEffect(() => {
    if (!giftCanvasRef.current || !backgroundCanvasRef.current) return;
    const giftCanvas = giftCanvasRef.current;
    giftCanvas.width = GIFT_VIDEO_WIDTH;
    giftCanvas.height = GIFT_VIDEO_HEIGHT;

    if (!giftVideoRef.current) {
      const giftVideo = document.createElement('video');
      giftVideoRef.current = giftVideo;

      const webmsrc = document.createElement('source');
      webmsrc.type = 'video/webm';
      webmsrc.src = 'https://cleanhome-web.cleaninglab.co.kr/brandsite/gift/webm/gift-out.webm';

      const mp4src = document.createElement('source');
      mp4src.type = 'video/mp4';
      mp4src.src = 'https://cleanhome-web.cleaninglab.co.kr/brandsite/gift/gift-out.mp4';

      giftVideo.appendChild(webmsrc);
      giftVideo.appendChild(mp4src);

      giftVideo.muted = true;
      giftVideo.loop = true;
      giftVideo.playsInline = true;
      giftVideo.autoplay = true;
      giftVideo.crossOrigin = 'anonymous';
    }

    const giftVideo = giftVideoRef.current;
    giftVideo.addEventListener('loadeddata', () => {
      giftVideo.play();
    });
    drawGiftCanvas(giftVideo);
  }, [media]);

  const drawGiftCanvas = (video: HTMLVideoElement) => {
    if (!giftCanvasRef.current) return;
    const giftCanvas = giftCanvasRef.current;
    const giftCtx = giftCanvas.getContext('2d');
    giftCtx?.drawImage(video, 0, 0, GIFT_VIDEO_WIDTH, GIFT_VIDEO_HEIGHT);
    const bgColor = giftCtx?.getImageData(0, 0, 1, 1).data;

    if (!backgroundCanvasRef.current) return;
    const backgroundCanvas = backgroundCanvasRef.current;
    const backgroundCtx = backgroundCanvas.getContext('2d');
    const hex = rgbToHex(bgColor![0], bgColor![1], bgColor![2]);
    backgroundCtx!.fillStyle = bgColor![0] ? hex : '#15384b';
    backgroundCtx!.fillRect(0, 0, backgroundCanvas.width, backgroundCanvas.height);

    requestAnimationFrame(() => drawGiftCanvas(video));
  };

  return (
    <>
      {/* <video
        width="100%"
        autoPlay
        muted
        loop
        playsInline
        src="https://cleanhome-web.cleaninglab.co.kr/brandsite/gift/webm/gift-out.webm"
      />
      <video
        width="100%"
        autoPlay
        muted
        loop
        playsInline
        src="https://cleanhome-web.cleaninglab.co.kr/brandsite/gift/gift-out.mp4"
      /> */}
      <Container>
        <HeroImageWrapper>
          <BackgroundCanvas ref={backgroundCanvasRef} />
          <VideoBlur />
        </HeroImageWrapper>
        <FrontWrapper>
          <Front>
            <ImageContainer>
              <ImageWrapper
                initial={{ opacity: 0 }}
                animate={{ y: [10, 10, 0], opacity: [0, 0, 1] }}
                transition={{ duration: 1.2, times: [0, 0.66, 1], ease: [0.65, 0, 0.35, 1] }}
              >
                <GiftCanvas ref={giftCanvasRef} />
                <VideoBlur />
              </ImageWrapper>
            </ImageContainer>
            <TitleWrapper
              initial={{ opacity: 0 }}
              animate={{ y: [10, 10, 0], opacity: [0, 0, 1] }}
              transition={{ duration: 1, times: [0, 0.6, 1], ease: [0.65, 0, 0.35, 1] }}
            >
              <SubTitle>청연 선물하기</SubTitle>
              <FakeHeroTitle>청소도 선물이 가능해요</FakeHeroTitle>
              <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
              <TitleTablet>{TitleTabletSvg}</TitleTablet>
              <TitleMobile>{TitleMobileSvg}</TitleMobile>
            </TitleWrapper>
            <HeroBottom
              initial={{ opacity: 0 }}
              animate={{ y: [10, 10, 0], opacity: [0, 0, 1] }}
              transition={{ duration: 1.2, times: [0, 0.66, 1], ease: [0.65, 0, 0.35, 1] }}
            >
              <Description>
                집안일 할 시간에 자유로운 시간을 갖는 경험을 선물하세요. 소중한 사람에게 시간을 선물하세요.
              </Description>
              <Link href="https://res.cleaninglab.co.kr/u/web_to_app.html?type=GIFT_MAIN" passHref>
                <Button
                  onClick={(e) => {
                    const platform = getPlatform();

                    if (platform === 'mobile' || platform === 'tablet') return;

                    e.preventDefault();
                    setIsAppDownloadModalVisible(true);
                  }}
                >
                  앱에서 선물하기
                </Button>
              </Link>
            </HeroBottom>
          </Front>
        </FrontWrapper>
        <AppDownloadModal2
          visible={isAppDownloadModalVisible}
          setVisible={setIsAppDownloadModalVisible}
          hasBodyScrollLock
        />
      </Container>
    </>
  );
}

const Container = styled.section`
  position: relative;
  width: 100%;
  height: 700px;
  background: #15384b;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    height: 600px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    height: 620px;
  }
`;

const ImageWrapper = styled(motion.div)`
  position: relative;
`;

const ImageContainer = styled.div`
  position: absolute;
  width: fit-content;
  top: 50%;
  left: 28%;
  transform: translateY(-50%) scale(${GIFT_SCALE.desktop});

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    left: 0%;
    transform: translateY(-50%) scale(${GIFT_SCALE.tablet});
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    top: auto;
    left: auto;
    bottom: -140%;
    right: 0%;
    transform: translateX(43%) scale(${GIFT_SCALE.mobile});
  }
`;

const BackgroundCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
  touch-action: none;
  pointer-events: none;
  background: #15384b;
`;

const GiftCanvas = styled.canvas`
  object-fit: cover;
  user-select: none;
  touch-action: none;
  pointer-events: none;
  background: #15384b;
  /* scale: ${GIFT_SCALE.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    scale: ${GIFT_SCALE.tablet};
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    scale: ${GIFT_SCALE.mobile};
  } */
`;

const VideoBlur = styled.div`
  position: absolute;
  inset: 0;
`;

const HeroImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const FrontWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0 24px;
`;

const Front = styled.div`
  position: relative;
  width: 100%;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.desktop};
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.tablet};
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    /* max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.mobile}; */
  }
`;

const TitleWrapper = styled(motion.div)`
  position: relative;
  margin-top: 214px;
  margin-bottom: 36px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-top: 184px;
    margin-bottom: 24px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-top: 132px;
    margin-bottom: 24px;
  }
`;

const FakeHeroTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }
`;

const TitleMobile = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: block;
  }
`;

const SubTitle = styled.h2`
  color: #00bed6;
  font-weight: 500;
  font-size: 26px;
  line-height: 140%;
  margin-bottom: 22px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 22px;
    margin-bottom: 10px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-size: 17px;
    margin-bottom: 19px;
  }
`;

const HeroBottom = styled(motion.div)`
  position: relative;
`;

const Description = styled.p`
  color: #ffffff;
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  word-break: keep-all;
  max-width: 480px;
  margin-bottom: 50px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;
    margin-bottom: 40px;
    max-width: 400px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    max-width: 241px;
    font-weight: 400;
  }
`;

const Button = styled.a`
  display: inline-block;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  border: 1px solid #ffffff;
  border-radius: 8px;
  padding: 12px 20px;
  transition: 0.2s;
  color: #ffffff;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 15px;
    padding: 10px 16px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 9px 16px;
  }

  &:hover {
    color: rgba(0, 0, 0, 0.85);
    background: #ffffff;
  }
`;

const TitleDesktopSvg = (
  <svg width="511" height="54" viewBox="0 0 511 54" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M22.5926 6.172V0.609999H7.0406V6.172H22.5926ZM34.9046 41.38C34.9046 44.566 31.9346 46.564 27.9926 46.564C23.9426 46.564 21.1346 44.566 21.1346 41.38C21.1346 38.248 23.9426 36.25 27.9926 36.25C31.9346 36.25 34.9046 38.248 34.9046 41.38ZM41.8706 30.58V0.555999H35.2826V15.73H27.5606V21.13H35.2826V30.58H41.8706ZM27.9926 14.38V8.818H1.5866V14.38H11.1986C10.3886 20.158 5.4746 23.722 0.182602 25.342L3.1526 30.58C8.4446 28.96 13.3586 24.802 14.9786 21.13C16.6526 24.748 22.1606 28.474 26.6966 29.554L29.5046 24.208C23.8886 22.804 19.4606 19.888 18.7046 14.38H27.9926ZM41.4386 41.38C41.4386 35.17 35.7686 30.742 27.9926 30.742C20.2166 30.742 14.5466 35.17 14.5466 41.38C14.5466 47.806 20.2166 52.072 27.9926 52.072C35.7686 52.072 41.4386 47.806 41.4386 41.38ZM93.2204 40.624V35.116H74.0504V24.91H67.4084V35.116H48.1844V40.624H93.2204ZM66.8684 1.852V4.822C66.8684 13.57 59.4704 19.078 51.4784 21.238L54.4484 26.908C61.8464 24.64 67.9484 20.266 70.7024 14.38C73.6184 20.05 79.3964 24.424 87.0104 26.8L89.9804 21.184C82.0964 18.916 74.5364 13.57 74.5364 4.822V1.852H66.8684ZM141.816 40.57V35.062H122.646V25.882H135.12V20.266H110.604V8.44H134.85V2.932H103.962V25.882H116.004V35.062H96.7802V40.57H141.816ZM189.077 24.694C183.353 22.696 177.845 15.838 177.845 8.548V2.5H170.879V8.656C170.879 16.378 165.857 23.128 159.377 26.206L163.265 31.12C168.449 28.69 172.661 23.452 174.497 18.97C176.549 23.29 181.139 27.88 185.621 29.716L189.077 24.694ZM202.685 49.75V44.08H179.303V32.038H172.769V49.75H202.685ZM201.713 35.71V0.663998H195.125V11.086H185.675V16.648H195.125V35.71H201.713ZM238.969 12.922H222.12V6.172H238.969V12.922ZM245.61 18.376V0.771996H215.478V18.376H245.61ZM246.852 51.694V46.402H221.742V43.648H245.557V30.526H233.892V26.962H253.063V21.508H208.026V26.962H227.25V30.526H215.208V35.872H239.023V38.464H215.208V51.694H246.852ZM257.81 17.296C257.81 25.558 263.534 31.066 271.364 31.066C279.302 31.066 284.972 25.558 284.972 17.296C284.972 8.98 279.302 3.472 271.364 3.472C263.534 3.472 257.81 8.98 257.81 17.296ZM264.398 17.242C264.398 12.382 267.368 9.304 271.364 9.304C275.414 9.304 278.33 12.382 278.33 17.242C278.33 22.156 275.414 25.18 271.364 25.18C267.368 25.18 264.398 22.156 264.398 17.242ZM291.83 45.214H298.364V0.663998H291.83V45.214ZM321.595 3.742V9.358H337.957C337.201 17.188 330.829 25.396 319.273 29.662L322.891 35.062C337.687 28.96 344.923 18.268 344.869 3.742H321.595ZM366.145 20.428V14.92H358.207V0.663998H351.511V45.214H358.207V20.428H366.145ZM398.055 42.46C398.055 45.592 394.815 47.752 390.387 47.752C385.797 47.752 382.719 45.592 382.719 42.46C382.719 39.382 385.851 37.168 390.387 37.168C394.815 37.168 398.055 39.382 398.055 42.46ZM404.481 42.46C404.481 35.764 398.217 31.606 390.387 31.606C382.773 31.606 376.293 35.71 376.293 42.46C376.293 49.696 383.259 53.26 390.387 53.26C397.461 53.26 404.481 49.642 404.481 42.46ZM412.905 28.96V23.398H367.869V28.96H412.905ZM375.375 1.15V19.294H405.939V13.678H382.017V1.15H375.375ZM433.42 26.8C433.42 29.338 431.638 31.174 429.046 31.174C426.184 31.174 424.456 29.338 424.456 26.8C424.456 24.424 426.184 22.48 429.046 22.48C431.638 22.48 433.42 24.424 433.42 26.8ZM434.5 7.306V1.798H422.242V7.306H434.5ZM440.278 15.406V9.952H416.032V15.406H440.278ZM439.468 26.8C439.468 20.914 434.77 17.08 429.046 17.08C423.268 17.08 418.462 20.914 418.462 26.8C418.462 32.632 423.268 36.628 429.046 36.628C435.094 36.628 439.468 32.632 439.468 26.8ZM459.394 45.16V0.663998H452.914V16.216H448.918V1.312H442.438V43.324H448.918V21.67H452.914V45.16H459.394ZM479.154 14.434C479.154 10.222 482.826 7.306 487.578 7.306C492.33 7.306 496.056 10.222 496.056 14.434C496.056 18.754 492.33 21.562 487.578 21.562C482.826 21.562 479.154 18.754 479.154 14.434ZM510.096 40.678V35.116H498.81V27.826H492.222V35.116H482.934V27.826H476.346V35.116H465.06V40.678H510.096ZM472.296 14.434C472.296 21.778 478.506 27.232 487.578 27.232C496.65 27.232 502.914 21.778 502.914 14.434C502.914 7.036 496.65 1.528 487.578 1.528C478.506 1.528 472.296 7.036 472.296 14.434Z"
      fill="white"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="322" height="34" viewBox="0 0 322 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.946 3.812V0.309999H5.154V3.812H14.946ZM22.698 25.98C22.698 27.986 20.828 29.244 18.346 29.244C15.796 29.244 14.028 27.986 14.028 25.98C14.028 24.008 15.796 22.75 18.346 22.75C20.828 22.75 22.698 24.008 22.698 25.98ZM27.084 19.18V0.276H22.936V9.83H18.074V13.23H22.936V19.18H27.084ZM18.346 8.98V5.478H1.72V8.98H7.772C7.262 12.618 4.168 14.862 0.836 15.882L2.706 19.18C6.038 18.16 9.132 15.542 10.152 13.23C11.206 15.508 14.674 17.854 17.53 18.534L19.298 15.168C15.762 14.284 12.974 12.448 12.498 8.98H18.346ZM26.812 25.98C26.812 22.07 23.242 19.282 18.346 19.282C13.45 19.282 9.88 22.07 9.88 25.98C9.88 30.026 13.45 32.712 18.346 32.712C23.242 32.712 26.812 30.026 26.812 25.98ZM59.4153 25.504V22.036H47.3453V15.61H43.1633V22.036H31.0593V25.504H59.4153ZM42.8233 1.092V2.962C42.8233 8.47 38.1653 11.938 33.1333 13.298L35.0033 16.868C39.6613 15.44 43.5033 12.686 45.2373 8.98C47.0733 12.55 50.7113 15.304 55.5053 16.8L57.3753 13.264C52.4113 11.836 47.6513 8.47 47.6513 2.962V1.092H42.8233ZM90.0127 25.47V22.002H77.9427V16.222H85.7967V12.686H70.3607V5.24H85.6267V1.772H66.1787V16.222H73.7607V22.002H61.6567V25.47H90.0127ZM119.769 15.474C116.165 14.216 112.697 9.898 112.697 5.308V1.5H108.311V5.376C108.311 10.238 105.149 14.488 101.069 16.426L103.517 19.52C106.781 17.99 109.433 14.692 110.589 11.87C111.881 14.59 114.771 17.48 117.593 18.636L119.769 15.474ZM128.337 31.25V27.68H113.615V20.098H109.501V31.25H128.337ZM127.725 22.41V0.343999H123.577V6.906H117.627V10.408H123.577V22.41H127.725ZM151.183 8.062H140.575V3.812H151.183V8.062ZM155.365 11.496V0.411998H136.393V11.496H155.365ZM156.147 32.474V29.142H140.337V27.408H155.331V19.146H147.987V16.902H160.057V13.468H131.701V16.902H143.805V19.146H136.223V22.512H151.217V24.144H136.223V32.474H156.147ZM163.046 10.816C163.046 16.018 166.65 19.486 171.58 19.486C176.578 19.486 180.148 16.018 180.148 10.816C180.148 5.58 176.578 2.112 171.58 2.112C166.65 2.112 163.046 5.58 163.046 10.816ZM167.194 10.782C167.194 7.722 169.064 5.784 171.58 5.784C174.13 5.784 175.966 7.722 175.966 10.782C175.966 13.876 174.13 15.78 171.58 15.78C169.064 15.78 167.194 13.876 167.194 10.782ZM184.466 28.394H188.58V0.343999H184.466V28.394ZM203.207 2.282V5.818H213.509C213.033 10.748 209.021 15.916 201.745 18.602L204.023 22.002C213.339 18.16 217.895 11.428 217.861 2.282H203.207ZM231.257 12.788V9.32H226.259V0.343999H222.043V28.394H226.259V12.788H231.257ZM251.348 26.66C251.348 28.632 249.308 29.992 246.52 29.992C243.63 29.992 241.692 28.632 241.692 26.66C241.692 24.722 243.664 23.328 246.52 23.328C249.308 23.328 251.348 24.722 251.348 26.66ZM255.394 26.66C255.394 22.444 251.45 19.826 246.52 19.826C241.726 19.826 237.646 22.41 237.646 26.66C237.646 31.216 242.032 33.46 246.52 33.46C250.974 33.46 255.394 31.182 255.394 26.66ZM260.698 18.16V14.658H232.342V18.16H260.698ZM237.068 0.649999V12.074H256.312V8.538H241.25V0.649999H237.068ZM273.615 16.8C273.615 18.398 272.493 19.554 270.861 19.554C269.059 19.554 267.971 18.398 267.971 16.8C267.971 15.304 269.059 14.08 270.861 14.08C272.493 14.08 273.615 15.304 273.615 16.8ZM274.295 4.526V1.058H266.577V4.526H274.295ZM277.933 9.626V6.192H262.667V9.626H277.933ZM277.423 16.8C277.423 13.094 274.465 10.68 270.861 10.68C267.223 10.68 264.197 13.094 264.197 16.8C264.197 20.472 267.223 22.988 270.861 22.988C274.669 22.988 277.423 20.472 277.423 16.8ZM289.969 28.36V0.343999H285.889V10.136H283.373V0.751999H279.293V27.204H283.373V13.57H285.889V28.36H289.969ZM302.411 9.014C302.411 6.362 304.723 4.526 307.715 4.526C310.707 4.526 313.053 6.362 313.053 9.014C313.053 11.734 310.707 13.502 307.715 13.502C304.723 13.502 302.411 11.734 302.411 9.014ZM321.893 25.538V22.036H314.787V17.446H310.639V22.036H304.791V17.446H300.643V22.036H293.537V25.538H321.893ZM298.093 9.014C298.093 13.638 302.003 17.072 307.715 17.072C313.427 17.072 317.371 13.638 317.371 9.014C317.371 4.356 313.427 0.888 307.715 0.888C302.003 0.888 298.093 4.356 298.093 9.014Z"
      fill="white"
    />
  </svg>
);

const TitleMobileSvg = (
  <svg width="189" height="78" viewBox="0 0 189 78" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.946 3.8159V0.313905H5.154V3.8159H14.946ZM22.698 25.9839C22.698 27.9899 20.828 29.2479 18.346 29.2479C15.796 29.2479 14.028 27.9899 14.028 25.9839C14.028 24.0119 15.796 22.7539 18.346 22.7539C20.828 22.7539 22.698 24.0119 22.698 25.9839ZM27.084 19.1839V0.279906H22.936V9.83391H18.074V13.2339H22.936V19.1839H27.084ZM18.346 8.98391V5.48191H1.72V8.98391H7.772C7.262 12.6219 4.168 14.8659 0.836 15.8859L2.706 19.1839C6.038 18.1639 9.132 15.5459 10.152 13.2339C11.206 15.5119 14.674 17.8579 17.53 18.5379L19.298 15.1719C15.762 14.2879 12.974 12.4519 12.498 8.98391H18.346ZM26.812 25.9839C26.812 22.0739 23.242 19.2859 18.346 19.2859C13.45 19.2859 9.88 22.0739 9.88 25.9839C9.88 30.0299 13.45 32.7159 18.346 32.7159C23.242 32.7159 26.812 30.0299 26.812 25.9839ZM59.4153 25.5079V22.0399H47.3453V15.6139H43.1633V22.0399H31.0593V25.5079H59.4153ZM42.8233 1.09591V2.96591C42.8233 8.47391 38.1653 11.9419 33.1333 13.3019L35.0033 16.8719C39.6613 15.4439 43.5033 12.6899 45.2373 8.98391C47.0733 12.5539 50.7113 15.3079 55.5053 16.8039L57.3753 13.2679C52.4113 11.8399 47.6513 8.47391 47.6513 2.96591V1.09591H42.8233ZM90.0127 25.4739V22.0059H77.9427V16.2259H85.7967V12.6899H70.3607V5.2439H85.6267V1.7759H66.1787V16.2259H73.7607V22.0059H61.6567V25.4739H90.0127ZM119.769 15.4779C116.165 14.2199 112.697 9.90191 112.697 5.31191V1.5039H108.311V5.3799C108.311 10.2419 105.149 14.4919 101.069 16.4299L103.517 19.5239C106.781 17.9939 109.433 14.6959 110.589 11.8739C111.881 14.5939 114.771 17.4839 117.593 18.6399L119.769 15.4779ZM128.337 31.2539V27.6839H113.615V20.1019H109.501V31.2539H128.337ZM127.725 22.4139V0.347905H123.577V6.90991H117.627V10.4119H123.577V22.4139H127.725ZM151.183 8.0659H140.575V3.8159H151.183V8.0659ZM155.365 11.4999V0.415904H136.393V11.4999H155.365ZM156.147 32.4779V29.1459H140.337V27.4119H155.331V19.1499H147.987V16.9059H160.057V13.4719H131.701V16.9059H143.805V19.1499H136.223V22.5159H151.217V24.1479H136.223V32.4779H156.147ZM163.046 10.8199C163.046 16.0219 166.65 19.4899 171.58 19.4899C176.578 19.4899 180.148 16.0219 180.148 10.8199C180.148 5.5839 176.578 2.1159 171.58 2.1159C166.65 2.1159 163.046 5.5839 163.046 10.8199ZM167.194 10.7859C167.194 7.72591 169.064 5.78791 171.58 5.78791C174.13 5.78791 175.966 7.72591 175.966 10.7859C175.966 13.8799 174.13 15.7839 171.58 15.7839C169.064 15.7839 167.194 13.8799 167.194 10.7859ZM184.466 28.3979H188.58V0.347905H184.466V28.3979ZM1.924 46.2859V49.8219H12.226C11.75 54.7519 7.738 59.9199 0.462 62.6059L2.74 66.0059C12.056 62.1639 16.612 55.4319 16.578 46.2859H1.924ZM29.974 56.7919V53.3239H24.976V44.3479H20.76V72.3979H24.976V56.7919H29.974ZM50.0653 70.6639C50.0653 72.6359 48.0253 73.9959 45.2373 73.9959C42.3473 73.9959 40.4093 72.6359 40.4093 70.6639C40.4093 68.7259 42.3813 67.3319 45.2373 67.3319C48.0253 67.3319 50.0653 68.7259 50.0653 70.6639ZM54.1113 70.6639C54.1113 66.4479 50.1673 63.8299 45.2373 63.8299C40.4433 63.8299 36.3633 66.4139 36.3633 70.6639C36.3633 75.2199 40.7493 77.4639 45.2373 77.4639C49.6913 77.4639 54.1113 75.1859 54.1113 70.6639ZM59.4153 62.1639V58.6619H31.0593V62.1639H59.4153ZM35.7853 44.6539V56.0779H55.0293V52.5419H39.9673V44.6539H35.7853ZM72.3327 60.8039C72.3327 62.4019 71.2107 63.5579 69.5787 63.5579C67.7767 63.5579 66.6887 62.4019 66.6887 60.8039C66.6887 59.3079 67.7767 58.0839 69.5787 58.0839C71.2107 58.0839 72.3327 59.3079 72.3327 60.8039ZM73.0127 48.5299V45.0619H65.2947V48.5299H73.0127ZM76.6507 53.6299V50.1959H61.3847V53.6299H76.6507ZM76.1407 60.8039C76.1407 57.0979 73.1827 54.6839 69.5787 54.6839C65.9407 54.6839 62.9147 57.0979 62.9147 60.8039C62.9147 64.4759 65.9407 66.9919 69.5787 66.9919C73.3867 66.9919 76.1407 64.4759 76.1407 60.8039ZM88.6867 72.3639V44.3479H84.6067V54.1399H82.0907V44.7559H78.0107V71.2079H82.0907V57.5739H84.6067V72.3639H88.6867ZM101.128 53.0179C101.128 50.3659 103.44 48.5299 106.432 48.5299C109.424 48.5299 111.77 50.3659 111.77 53.0179C111.77 55.7379 109.424 57.5059 106.432 57.5059C103.44 57.5059 101.128 55.7379 101.128 53.0179ZM120.61 69.5419V66.0399H113.504V61.4499H109.356V66.0399H103.508V61.4499H99.36V66.0399H92.254V69.5419H120.61ZM96.81 53.0179C96.81 57.6419 100.72 61.0759 106.432 61.0759C112.144 61.0759 116.088 57.6419 116.088 53.0179C116.088 48.3599 112.144 44.8919 106.432 44.8919C100.72 44.8919 96.81 48.3599 96.81 53.0179Z"
      fill="white"
    />
  </svg>
);
